import React, { useEffect, useState } from "react";
import { useLanguage, shouldOfferSpanishBanner, dismissSpanishBanner } from "../i18n";

/** Small toggle: 🇺🇸 EN | 🇲🇽 ES */
export function LanguageToggle({ className = "" }) {
  const { lang, setLang } = useLanguage();
  return (
    <div
      className={`inline-flex items-center text-xs font-semibold rounded-full border border-[#2D3748]/20 overflow-hidden bg-white/10 ${className}`}
      data-testid="language-toggle"
      role="group"
      aria-label="Language selector"
    >
      <button
        type="button"
        onClick={() => lang !== "en" && setLang("en")}
        className={`px-2.5 py-1 transition-colors ${
          lang === "en" ? "bg-[#C05621] text-white" : "text-white/80 hover:text-white"
        }`}
        aria-pressed={lang === "en"}
        aria-label="English"
        data-testid="language-toggle-en"
      >
        EN
      </button>
      <button
        type="button"
        onClick={() => lang !== "es" && setLang("es")}
        className={`px-2.5 py-1 transition-colors ${
          lang === "es" ? "bg-[#C05621] text-white" : "text-white/80 hover:text-white"
        }`}
        aria-pressed={lang === "es"}
        aria-label="Español"
        data-testid="language-toggle-es"
      >
        ES
      </button>
    </div>
  );
}

/** First-visit banner offered to browsers with es-* language. */
export function SpanishOfferBanner() {
  const { lang, setLang } = useLanguage();
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Delay briefly so we don't flash before React hydration
    const id = setTimeout(() => {
      if (shouldOfferSpanishBanner(lang)) setShow(true);
    }, 300);
    return () => clearTimeout(id);
  }, [lang]);

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm bg-[#2D3748] text-white rounded-xl shadow-2xl border border-[#C05621]/30 p-4 z-[60] animate-in slide-in-from-bottom duration-300"
      data-testid="spanish-offer-banner"
      role="dialog"
      aria-label="Offer Spanish version"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <p className="text-sm font-semibold mb-1">¿Prefieres español?</p>
          <p className="text-xs text-white/75">
            También tenemos nuestro sitio en español con los mismos precios y financiamiento.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={() => {
                setShow(false);
                setLang("es");
              }}
              className="px-3 py-1.5 bg-[#C05621] hover:bg-[#9C4221] text-white text-xs font-bold rounded-lg transition-colors"
              data-testid="spanish-banner-accept"
            >
              Ver en español
            </button>
            <button
              type="button"
              onClick={() => {
                dismissSpanishBanner();
                setShow(false);
              }}
              className="px-3 py-1.5 text-white/70 hover:text-white text-xs font-medium transition-colors"
              data-testid="spanish-banner-dismiss"
            >
              No, gracias
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            dismissSpanishBanner();
            setShow(false);
          }}
          className="text-white/40 hover:text-white/80 transition-colors leading-none text-lg -mt-1"
          aria-label="Close"
          data-testid="spanish-banner-close"
        >
          &times;
        </button>
      </div>
    </div>
  );
}
